import { createContext, ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import useSWR from "swr";
import { fetchPostWithParams } from "lib/fetcher";
import { startTelemery } from "../lib/openTelemery";
import { linkRoleAccess } from "ui/dashboard/link-role-access";

export type User = {
  id: string;
  email: string;
  accountName: string;
  orgId: string;
  role: string;
  token: string;
  name: string;
  phoneNumber: string;
  organization: {
    enableMonitoring: boolean;
    timezone?: string;
  };
  timezone?: string;
  source?: string;
};

type Token = {
  user_id: string;
  email: string;
  exp: number;
  org_id: string;
  role: string;
};

type TypeAuthContext = {
  authenticated: boolean;
  setAuthenticated: (newState: boolean) => void;
  user: User;
  setUser: (newState: User) => void;
  privileges: string[];
  setPrivileges: (newState: string[]) => void;
};

const initialUser = {
  id: "",
  email: "",
  accountName: "",
  orgId: "",
  role: "",
  token: "",
  name: "",
  phoneNumber: "",
  organization: null,
};

const initialValue = {
  authenticated: false,
  setAuthenticated: () => {},
  user: initialUser,
  setUser: () => {},
  privileges: [],
  setPrivileges: () => {},
};

const token = Cookies.get("token");
const accountName = Cookies.get("accountName");
const name = Cookies.get("name");
const enableMonitoring = Cookies.get("enableMonitoring");
const phoneNumber = Cookies.get("phoneNumber");
if (token) {
  const tokenDecoded = jwtDecode<Token>(token!);
  console.log("tokenDecoded = ", tokenDecoded);
  if (Date.now() > tokenDecoded.exp * 1000) {
    initialValue.authenticated = false;
  } else {
    console.log("exporter registered");
    initialValue.authenticated = true;
    startTelemery(token);
    initialValue.user = {
      id: tokenDecoded.user_id,
      email: tokenDecoded.email,
      accountName: accountName,
      orgId: tokenDecoded.org_id,
      role: tokenDecoded.role,
      token: token,
      name: name,
      phoneNumber: phoneNumber,
      organization: {
        enableMonitoring: enableMonitoring === "true" ? true : false,
      },
    };
  }
}

export const AuthContext = createContext<TypeAuthContext>(initialValue);

export default function AuthProvider({ children }: { children?: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(
    initialValue.authenticated,
  );
  const [user, setUser] = useState(initialValue.user);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [privileges, setPrivileges] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // calculate privileges for current page
    const pagePrivilege = [];
    const linkItem = linkRoleAccess.find((_linkItem) => {
      return _linkItem.path === location.pathname;
    });
    if (linkItem?.public) return;
    if (linkItem?.privilege) {
      linkItem.privilege.forEach((item: any) => {
        if (item.role.includes(user.role)) {
          pagePrivilege.push(item.name);
        }
      });
    }
    // calculate public privileges
    const publicPrivileges = [];
    const publicPrivilegesRaw = linkRoleAccess.find((_linkItem) => {
      return _linkItem.path === "/";
    })?.privilege;
    publicPrivilegesRaw.forEach((item: any) => {
      if (item.role.includes(user.role)) {
        publicPrivileges.push(item.name);
      }
    });
    const result = Array.from(new Set([...pagePrivilege, ...publicPrivileges]));
    if (JSON.stringify(privileges) !== JSON.stringify(result)) {
      setPrivileges(result);
    }
  }, [location.pathname, user]);

  function handleSignOut() {
    Cookies.remove("token", { domain: "." + window.location.hostname });
    setAuthenticated(false);
    navigate("/login");
  }
  useEffect(() => {
    const handler = function (event) {
      if (event.data === "logout") {
        handleSignOut();
      }
    };
    window.addEventListener("message", handler);
    //return window.removeEventListener("message", handler);
  }, []);
  async function validateToken() {
    const token = Cookies.get("token")!;
    if (token) {
      const tokenExp = jwtDecode(token).exp!;
      const expires_at = Math.floor(
        Math.floor(Date.now() / 1000) +
          (tokenExp - Math.floor(Date.now() / 1000)) / 2,
      );
      if (Date.now() > expires_at * 1000) {
        //refresh token
        setShouldFetch(true);
      } else {
        // notify logged
        window.parent.postMessage({ token: token, type: "autoLogin" }, "*");
      }
    }
  }
  useEffect(() => {
    validateToken();
  }, []);

  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`/auth/refresh`, user.token] : null,
    ([url, token]) => fetchPostWithParams(url, token),
  );
  data &&
    data.token &&
    Cookies.set("token", data.token, {
      expires: 1,
      domain: "." + window.location.hostname,
    });
  data &&
    data.token &&
    window.parent.postMessage({ token: token, type: "autoLogin" }, "*");
  return (
    <AuthContext.Provider
      value={{
        authenticated,
        setAuthenticated,
        user,
        setUser,
        privileges,
        setPrivileges,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
